import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, MessageSquare, Calendar, FileText, LayoutGrid } from "lucide-react";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import { FORM } from "@/constants/testIds";

const CAPITAL_OPTIONS = [
  { v: "250k-500k", l: "$250,000 a $499,999", legacy: "500k-1m" },
  { v: "500k-1m", l: "$500,000 a $999,999", legacy: "500k-1m" },
  { v: "1m-2.5m", l: "$1,000,000 a $2,499,999", legacy: "1m-3m" },
  { v: "2.5m+", l: "$2,500,000 o más", legacy: "3m-10m" },
];

const TIMING_OPTIONS = [
  { v: "30d", l: "En los próximos 30 días" },
  { v: "90d", l: "En los próximos 90 días" },
  { v: "6m", l: "Durante los próximos 6 meses" },
  { v: "evaluando", l: "Solo estoy evaluando" },
];

const FOLLOW_UP_OPTIONS = [
  { v: "whatsapp", l: "Recibir información por WhatsApp", icon: MessageSquare },
  { v: "meeting", l: "Agendar una reunión privada", icon: Calendar },
  { v: "projects", l: "Conocer los proyectos", icon: LayoutGrid },
  { v: "dossier", l: "Recibir el dossier", icon: FileText },
];

const inputCls =
  "w-full bg-white border border-[#0E0E10]/12 focus:border-[#FFBF57] outline-none px-4 py-3 text-base text-[#0E0E10] placeholder-[#8A8890] rounded-xl transition-colors";

export default function Qualification() {
  useSeo({ title: "Evalúa tu participación · Cuestionario privado", description: "Completa el cuestionario privado para evaluar tu participación en el Proyecto Diamante de Rubí Perfumería y agendar una conversación 1:1 con dirección.", path: "/calificacion" });
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const projectCode = sp.get("p");
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    capital_range: "",
    timing: "",
    name: "",
    email: "",
    phone: "",
    consent_privacy: true,
    follow_up: "",
    project_code: projectCode || null,
    source: "landing",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return !!form.capital_range;
    if (step === 1) return !!form.timing;
    if (step === 2) return form.name.trim() && form.email.trim() && form.phone.trim() && form.consent_privacy;
    if (step === 3) return !!form.follow_up;
    return false;
  };

  const submit = async (followUpValue) => {
    setSubmitting(true);
    const legacyMap = { "250k-500k": "500k-1m", "500k-1m": "500k-1m", "1m-2.5m": "1m-3m", "2.5m+": "3m-10m" };
    const payload = {
      ...form,
      follow_up: followUpValue || form.follow_up,
      // Legacy fields required by backend (kept for backward compat)
      capital_estimated: legacyMap[form.capital_range] || form.capital_range,
      city: "",
      horizon: "medio",
      expectation: "",
      risk_tolerance: "media",
    };
    try {
      const res = await api.post("/public/leads", payload);
      setResult({ ...res.data, follow_up: followUpValue || form.follow_up });
      toast.success("Solicitud recibida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el formulario");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-[#FAF7EF] text-[#0E0E10]">
        <TopBar theme="light" />
        <section className="max-w-2xl mx-auto px-6 py-16 lg:py-24" data-testid={FORM.success}>
          <div className="bg-white border border-[#0E0E10]/10 rounded-3xl p-10 shadow-sm">
            <div className="w-14 h-14 rounded-full bg-[#FFBF57]/8 flex items-center justify-center">
              <Check size={24} strokeWidth={1.75} className="text-[#FFBF57]" />
            </div>
            <p className="eyebrow mt-8">Solicitud recibida</p>
            <h1 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">Tu solicitud ha sido recibida</h1>
            <p className="text-[#55555C] mt-6 leading-relaxed">
              Revisaremos tu perfil para identificar qué proyectos podrían ser compatibles contigo. Un asesor de Rubí te contactará en las próximas 24 horas hábiles.
            </p>
            <div className="mt-8 pt-6 border-t border-[#0E0E10]/8 text-xs text-[#8A8890] font-mono">
              Referencia: <span className="text-[#0E0E10]">{result.lead_id}</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="https://wa.me/525550556647?text=Hola%2C%20acabo%20de%20aplicar%20a%20Proyecto%20Diamante" target="_blank" rel="noreferrer" className="btn-primary">
                <MessageSquare size={16} strokeWidth={1.75} /> Continuar por WhatsApp
              </a>
              <a href="https://wa.me/525550556647?text=Quiero%20agendar%20reuni%C3%B3n" target="_blank" rel="noreferrer" className="btn-outline">
                <Calendar size={16} strokeWidth={1.75} /> Agendar reunión
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const steps = ["Capital", "Temporalidad", "Contacto", "Siguiente paso"];
  const progressPct = Math.round(((step + (canNext() ? 1 : 0)) / steps.length) * 100);

  return (
    <div className="min-h-screen bg-[#FAF7EF] text-[#0E0E10]">
      <TopBar theme="light" />

      <section className="max-w-2xl mx-auto px-6 py-12 lg:py-16" data-testid={FORM.container}>
        <div className="mb-8">
          <p className="eyebrow">Aplicación privada · {step + 1} de {steps.length}</p>
          <h1 className="font-display text-3xl lg:text-5xl mt-3 leading-tight">
            {step === 0 && (<>¿Cuánto capital estás <span className="accent">considerando</span>?</>)}
            {step === 1 && (<>¿Cuándo considerarías <span className="accent">participar</span>?</>)}
            {step === 2 && (<>Tus <span className="accent">datos</span> de contacto.</>)}
            {step === 3 && (<>¿Cómo quieres <span className="accent">continuar</span>?</>)}
          </h1>
          <p className="mt-4 text-sm md:text-base text-[#3F3F46] leading-relaxed max-w-2xl" data-testid="qualification-intro">
            Este cuestionario privado nos permite conocer tu perfil como inversionista antes de la conversación 1:1 con la dirección de Rubí Perfumería. Toma menos de tres minutos: indica el capital que te interesa participar (desde $250,000 MXN), tu horizonte y tus prioridades, y te contactaremos para explicarte el modelo de Asociación en Participación, el Proyecto Diamante y los siguientes pasos hacia la firma de contrato. Tu información se resguarda bajo aviso de privacidad y no se comparte con terceros.
          </p>
        </div>
        <div className="mb-8">
          <div className="h-1.5 bg-[#0E0E10]/8 rounded-full overflow-hidden">
            <div className="h-full bg-[#FFBF57] rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="mt-3 flex justify-between text-[10px] font-mono uppercase tracking-widest text-[#8A8890]">
            {steps.map((s, i) => (
              <span key={i} className={i <= step ? "text-[#FFBF57]" : ""}>{s}</span>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#0E0E10]/10 rounded-3xl p-6 lg:p-10 shadow-sm" data-testid={FORM.step(step)}>
          {step === 0 && (
            <div className="grid gap-3">
              {CAPITAL_OPTIONS.map((o) => (
                <BigChoice
                  key={o.v}
                  active={form.capital_range === o.v}
                  onClick={() => set("capital_range", o.v)}
                  label={o.l}
                  testId={`${FORM.field("capital_range")}-${o.v}`}
                />
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-3">
              {TIMING_OPTIONS.map((o) => (
                <BigChoice
                  key={o.v}
                  active={form.timing === o.v}
                  onClick={() => set("timing", o.v)}
                  label={o.l}
                  testId={`${FORM.field("timing")}-${o.v}`}
                />
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="grid gap-4">
              <Field label="Nombre completo">
                <input className={inputCls} data-testid={FORM.field("name")} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ej. María Fernández" />
              </Field>
              <Field label="WhatsApp">
                <input className={inputCls} data-testid={FORM.field("phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+52 55 ..." />
              </Field>
              <Field label="Correo electrónico">
                <input className={inputCls} data-testid={FORM.field("email")} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="tu@correo.com" />
              </Field>
              <label className="flex items-start gap-3 cursor-pointer text-sm text-[#55555C] mt-2">
                <input type="checkbox" data-testid={FORM.field("consent_privacy")} checked={form.consent_privacy} onChange={(e) => set("consent_privacy", e.target.checked)} className="mt-1 accent-[#FFBF57]" />
                <span>He leído y acepto el <span className="underline text-[#FFBF57]">aviso de privacidad</span>. Autorizo a Rubí a contactarme sobre esta solicitud.</span>
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-3">
              {FOLLOW_UP_OPTIONS.map((o) => {
                const Icon = o.icon;
                return (
                  <button
                    key={o.v}
                    onClick={() => {
                      set("follow_up", o.v);
                      submit(o.v);
                    }}
                    disabled={submitting}
                    data-testid={`${FORM.field("follow_up")}-${o.v}`}
                    className={`w-full text-left flex items-center gap-4 px-5 py-4 border rounded-xl transition-colors ${form.follow_up === o.v ? "border-[#FFBF57] bg-[#FFBF57]/[0.04]" : "border-[#0E0E10]/12 hover:border-[#FFBF57]/40 hover:bg-[#FFBF57]/[0.02]"} disabled:opacity-60`}
                  >
                    <span className="w-10 h-10 rounded-full bg-[#FFBF57]/8 flex items-center justify-center text-[#FFBF57]">
                      <Icon size={18} strokeWidth={1.75} />
                    </span>
                    <span className="font-medium text-[#0E0E10] flex-1">{o.l}</span>
                    <ArrowRight size={16} strokeWidth={1.75} className="text-[#FFBF57]" />
                  </button>
                );
              })}
              {submitting && (
                <div className="text-center text-sm text-[#8A8890] mt-4">Enviando tu solicitud…</div>
              )}
            </div>
          )}

          {step < 3 && (
            <div className="mt-8 flex items-center justify-between">
              <button
                className="btn-outline disabled:opacity-40 !py-2 !px-4"
                disabled={step === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                data-testid={FORM.prev}
              >
                <ArrowLeft size={14} strokeWidth={1.75} /> Anterior
              </button>
              <button
                className="btn-primary disabled:opacity-40"
                disabled={!canNext()}
                onClick={() => setStep((s) => s + 1)}
                data-testid={FORM.next}
              >
                Continuar <ArrowRight size={14} strokeWidth={1.75} />
              </button>
            </div>
          )}
        </div>

        <p className="text-xs text-[#8A8890] mt-6 text-center font-mono">
          La participación está sujeta a evaluación, disponibilidad, riesgos y formalización contractual.
        </p>

        <div className="mt-8 text-center text-xs text-[#8A8890]">
          <Link to="/" className="hover:text-[#FFBF57]">← Volver al inicio</Link>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-[#8A8890] mb-2 block font-mono font-medium">{label}</label>
      {children}
    </div>
  );
}

function BigChoice({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left flex items-center gap-4 px-5 py-4 border rounded-xl transition-all ${active ? "border-[#FFBF57] bg-[#FFBF57]/[0.05] text-[#0E0E10]" : "border-[#0E0E10]/12 hover:border-[#FFBF57]/40 hover:bg-[#FFBF57]/[0.02] text-[#0E0E10]"}`}
    >
      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${active ? "border-[#FFBF57] bg-[#FFBF57]" : "border-[#8A8890]"}`}>
        {active && <span className="w-2 h-2 rounded-full bg-white" />}
      </span>
      <span className="font-medium">{label}</span>
    </button>
  );
}
